import React, {
    FunctionComponent,
    useState,
    useEffect,
    createRef,
    RefObject,
    useRef,
} from "react";
import { shallowEqual, useSelector } from "react-redux";
import { FiExternalLink } from "@react-icons/all-files/fi/FiExternalLink";
import { FiCheck } from "@react-icons/all-files/fi/FiCheck";
import { GiAchillesHeel } from "@react-icons/all-files/gi/GiAchillesHeel";
import styled from "styled-components";
import { Modal } from "antd";

import {
    ISegment,
    IProfileRecord,
    IRetailMatchChartData,
    IStoreScoreRecord,
    IBrand,
} from "../../../../Models/Models";
import {
    ReportCheckbox,
    ReportH4,
    ReportH1,
    ReportScrollableSection,
    ReportSectionNumber,
    ReportSectionHeader,
    AnimatedReportCard,
    ColSegInfoWrapper,
    ColSegInfo,
    Menu,
    MenuLink,
    SegCell,
    SegCirc,
    SegBody,
    SegHead,
    SegImg,
    SegDetails,
    SegLink,
    useWindowResize,
    SegCircTextL,
    SegCircTextSm,
    SegCellTextL,
    SegCellTextSm,
    SegCellTextSmB,
    getPrettyNumber,
    SegCellTRAction,
    SegRow,
    SegContentSecHeader,
    SegFooterSubText,
    SegCardBreakoutContainer,
    SegCardBreakoutItem,
    SegCardBreakoutVerticalBreak,
    findSegIcon,
    SegCardRow,
    SegCardCol,
    SegCardSpacer,
    SegCardHead,
    SegCardHeaderRow,
    determinePropensityText,
    EmptyResultsCell,
    BuxTooltip,
    PsychProfSumm,
} from "../../../Common";
import { CustomerBreakoutColors, deviceWidth } from "../../../../globalStyles";
import { featureFlags } from "../../../../Common/CurrentFeatureFlags";
import { RenderType } from "../../../../Enums/RenderType";
import {
    isOutputSelected,
    registerElement,
    registerRef,
} from "../../../Common/DigitalShare/DigitalShareGenerator";
import { DigitalShareAppType } from "../../../../Enums/DigitalShareAppType";
import { DigitalShareItemType } from "../../../../Enums/DigitalShareItemType";
import { RetailMatchPsychProfShare } from "./RetailMatchPsychProfShare";
import { brandOverviewRecord } from "../../../../Util/RetailMatch/RetailMatchUtil";
import { rmMosaicSegmentsSelector } from "../../../../Reducers/Selectors/RetailMatchSelectors";

interface RetailMatchPsychProfProps {
  locationAddress: string;
  brand: IBrand;
  chartData: IRetailMatchChartData;
  benchmarkRecord: IStoreScoreRecord;
  renderType: RenderType;
  app: DigitalShareAppType;
  itemType: DigitalShareItemType;
}

interface ISegmentStore {
  segments: ISegment[];
}

interface IProfRef {
  profile: IProfileRecord;
  ref: RefObject<HTMLDivElement>;
}

interface IProfCardContent {
  segment: ISegment;
  siteSeg: IProfileRecord;
  brandSeg: IProfileRecord;
}

export const RetailMatchPsychProf: FunctionComponent<RetailMatchPsychProfProps>
  = (props: RetailMatchPsychProfProps) => {
      const { locationAddress, brand, chartData, benchmarkRecord } = props;
      const brandName = brand.brandName;

      const mosaicSegments = useSelector(rmMosaicSegmentsSelector);

      const siteProfileForBrand = props.chartData.siteProfile.filter((record) => {
          return record.brandId === brand.brandId || !record.brandId;
      });

      const brandProfile = props.chartData.brandProfiles.find((record) => {
          return record.id === brand.brandId;
      })?.profile;

      const overviewRecord = brandOverviewRecord(
          brand.brandId,
          chartData.overview
      );

      const propensityToolTipText = `Visitor propensity compares the distribution of segments observed at the selection to the distribution of segments across all households in the selection’s trade area. The ratio between these percentages for each segment defines the propensity for households of that segment to be a visitor to the selected place (as compared to all other households). For example, a propensity of 2.5 indicates the segment is 2.5 times more likely than the average household in the trade area to visit the selection.  PRIMARY AND SECONDARY VISITOR HOUSEHOLDS HAVE A PROPENSITY OF GREATER THAN 1.0x AND REPRESENT MORE THAN 1% OF VISITORS.`;

      const [avgBrandHHCnt, segAvgBrandHHCnt] = useState<number>(null);
      const [open, setIsMenuOpen] = useState<boolean>();
      const [filters, setFilters] = useState<string[]>(["P", "S"]);
      const [profRefData, setProfRefData] = useState<IProfRef[]>(null);
      const [profRefDataRender, setProfRefDataRender]
      = useState<IProfRef[]>(null);
      const [buttonText, setButtonText] = useState<string>("");
      const [visibleModal, setVisibleModal] = useState<boolean>(false);
      const [modalContent, setModalContent] = useState<IProfCardContent>(null);
      const [showPropensityColumn, setShowPropensityColumn]
      = useState<boolean>(false);

      const windowSize = useWindowResize();
      const psychProfileRef = useRef();
      const psychSummaryRef = useRef();

      if (
          featureFlags.DigitalShare
      && props.renderType === RenderType.DIGITAL_SHARE
      && isOutputSelected(props.app, DigitalShareItemType.PsychographicProfile)
      ) {
          registerRef(
              props.app,
              DigitalShareItemType.PsychographicProfile,
              psychProfileRef
          );
          registerElement(
              props.app,
              DigitalShareItemType.PsychographicProfile,
              null,
              <RetailMatchPsychProfShare
                  brandName={brandName}
                  locationAddress={locationAddress}
                  showPropensityColumn={showPropensityColumn}
                  sampleCount={overviewRecord?.sampleCount}
                  data={getTopProfiles(5)}
                  segments={mosaicSegments}
                  benchmarkRecord={benchmarkRecord}
                  averageBrandHHCount={avgHHCnt()}
                  brandSegmentData={brandProfile}
              />
          );
      }

      if (
          featureFlags.DigitalShare
      && props.renderType === RenderType.DIGITAL_SHARE
      && isOutputSelected(props.app, DigitalShareItemType.PsychographicSummary)
      ) {
          registerRef(
              props.app,
              DigitalShareItemType.PsychographicSummary,
              psychSummaryRef
          );

          registerElement(
              props.app,
              DigitalShareItemType.PsychographicSummary,
              "",
              <PsychProfSumm segDets={mosaicSegments} topSegs={getTopProfiles(5)} />
          );
      }

      useEffect(() => {
          if (!avgBrandHHCnt && chartData) {
              segAvgBrandHHCnt(avgHHCnt());
          }

          if (!profRefData) {
              const tempArray: IProfRef[]
          = convertProfArrayToProfRefArray(siteProfileForBrand);
              setProfRefData(tempArray);
              const newSegments: IProfRef[] = filteredSegments(tempArray);
              setProfRefDataRender(newSegments);
          }
      }, []);

      useEffect(() => {
          if (profRefData) {
              const show: boolean = profRefData.every((data: IProfRef) => {
                  return data.profile.index != null;
              });
              setShowPropensityColumn(show);
          }
      }, [profRefData]);

      useEffect(() => {
          if (profRefData && filters.length > 0) {
              const newSegments: IProfRef[] = filteredSegments(profRefData);
              setProfRefDataRender(newSegments);
          } else if (profRefData && profRefDataRender) {
              setProfRefDataRender(profRefData);
          }
          formatButtonText();
      }, [filters, props.brand.brandId]);

      useEffect(() => {
          if (modalContent) {
              setVisibleModal(true);
          }
      }, [modalContent]);

      function getTopProfiles(numToGet: number): IProfileRecord[] {
          const allProfiles: IProfileRecord[] = profRefDataRender.map(
              (p: IProfRef) => p.profile
          );
          if (showPropensityColumn) {
              allProfiles.sort((a: IProfileRecord, b: IProfileRecord) => {
                  if (a.index > b.index) return -1;
                  if (a.index < b.index) return 1;
                  return 0;
              });
          } else {
              allProfiles.sort((a: IProfileRecord, b: IProfileRecord) => {
                  if (a.pct > b.pct) return -1;
                  if (a.pct < b.pct) return 1;
                  return 0;
              });
          }

          return allProfiles.slice(0, numToGet);
      }

      const filteredSegments = (inSegments: IProfRef[]): IProfRef[] => {
          const newSegments = inSegments.filter((profRef) => {
              const matchingBrandProfileSegment = brandProfile?.find(
                  (seg) => seg.seg === profRef.profile.seg
              );
              return filters.includes(matchingBrandProfileSegment?.core);
          });

          return newSegments;
      };

      const convertProfArrayToProfRefArray = (
          profArray: IProfileRecord[]
      ): IProfRef[] => {
          return profArray.map((prof: IProfileRecord) => {
              return { profile: prof, ref: createRef<HTMLDivElement>() };
          });
      };

      const setActive = (rowNum: number) => {
          const active = profRefData.find((profRef) =>
              profRef.ref.current?.classList.contains("active")
          );
          if (active) {
              active.ref.current.classList.remove("active");
          }
          if (rowNum !== null) {
              profRefData[rowNum].ref.current.classList.add("active");
          }
      };

      function avgHHCnt(): number {
          let totalSum = 0;
          for (const score of chartData.storeScores) {
              totalSum += score.hhCnt;
          }
          return totalSum / chartData.storeScores.length;
      }

      const formatButtonText = () => {
          let fmtStr = "All";
          if (filters.length > 1) {
              fmtStr = "Primary/Secondary";
          } else if (filters.includes("P")) {
              fmtStr = "Primary";
          } else if (filters.includes("S")) {
              fmtStr = "Secondary";
          }
          setButtonText(fmtStr);
      };

      const handleMenuClick = (filterVal: "P" | "S") => {
          setIsMenuOpen(false);

          if (filterVal) {
              // Toggle filter off if already selected
              if (filters.includes(filterVal)) {
                  const ind = filters.indexOf(filterVal);
                  filters.splice(ind, 1);
              } else {
                  filters.push(filterVal);
              }
              setFilters([...filters]);
          } else {
              setFilters([]);
          }
      };

      const calcHH = (hhPct: number, hhCnt: number): string => {
          return getPrettyNumber(Math.ceil(hhCnt * hhPct), 0);
      };

      const calcPctHH = (pct: number): string => {
          return getPrettyNumber(pct * 100, 2);
      };

      const StyledCheck = styled(FiCheck)`
      width: 17px;
      height: 17px;
      margin-right: 2px;
      flex-shrink: 0;
    `;

      const PctNumWrapper = styled.div`
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      margin: auto;
      height: 30px;
    `;

      return (
          <ReportScrollableSection id="RMPotentialCustomerSimilarity">
              <ReportSectionHeader>
                  <ReportSectionNumber>03</ReportSectionNumber>
                  <ReportH1>Psychographic Profile</ReportH1>
              </ReportSectionHeader>
              <AnimatedReportCard
                  scrollableParentSelector="#RetailMatchReportBody"
                  animateIn="zoomIn"
                  offset={100}
                  animateOnce={true}
              >
                  <ReportH4 style={{ lineHeight: "normal" }}>
                      {brandName}’s ideal customer segments based on lifestyle, life
            stage, behavioral, and consumer characteristics were identified
            based on mobile data observations of actual customers at the{" "}
                      {overviewRecord?.sampleCount.toLocaleString()} {brandName}’s
            locations.
                  </ReportH4>
                  <ColSegInfoWrapper>
                      <ColSegInfo
                          dotColor={CustomerBreakoutColors.P}
                          title={
                              <>
                                  <span>Primary</span> {brandName}’s Customer
                              </>
                          }
                          text={`Households 20+% more likely to visit ${brandName} than the average household`}
                      />
                      <ColSegInfo
                          dotColor={CustomerBreakoutColors.S}
                          title={
                              <>
                                  <span>Secondary</span> {brandName}’s Customer
                              </>
                          }
                          text={`Households up to 19% more likely to visit ${brandName} than the average household`}
                      />
                      <ColSegInfo
                          dotColor={CustomerBreakoutColors.N}
                          title={
                              <>
                                  <span>Incidental</span> {brandName}’s Customer
                              </>
                          }
                          text={`Households less likely to visit ${brandName} than the average household`}
                      />
                  </ColSegInfoWrapper>
                  <SegHead
                      className={`${profRefDataRender?.length >= 5 ? "" : "too-short"}`}
                  >
                      <SegRow>
                          <SegCell style={{ textAlign: "left", marginLeft: "-1px" }}>
                              {windowSize.width < deviceWidth.desktop ? (
                                  <Menu
                                      title={buttonText}
                                      type="filled"
                                      bgColor="black"
                                      color="white"
                                      isOpen={open}
                                      setIsMenuOpen={setIsMenuOpen}
                                      style={{ margin: 0 }}
                                      fontSize={11}
                                      align="left"
                                  >
                                      <MenuLink
                                          title={
                                              <>
                                                  {filters.includes("P") ? <StyledCheck /> : null}
                          Primary
                                              </>
                                          }
                                          clickCallback={() => {
                                              handleMenuClick("P");
                                          }}
                                          style={{ fontSize: "12px" }}
                                      />
                                      <MenuLink
                                          title={
                                              <>
                                                  {filters.includes("S") ? <StyledCheck /> : null}
                          Secondary
                                              </>
                                          }
                                          clickCallback={() => {
                                              handleMenuClick("S");
                                          }}
                                          style={{ fontSize: "12px" }}
                                      />
                                      <MenuLink
                                          title={
                                              <>{filters.length === 0 ? <StyledCheck /> : null}All</>
                                          }
                                          clickCallback={() => {
                                              handleMenuClick(null);
                                          }}
                                          style={{ fontSize: "12px" }}
                                      />
                                  </Menu>
                              ) : (
                                  <>
                                      <span style={{ marginRight: "5px" }}>Show:</span>
                                      <ReportCheckbox
                                          onChange={() => {
                                              handleMenuClick("P");
                                          }}
                                          checked={filters.includes("P") ? true : false}
                                      >
                      Primary
                                      </ReportCheckbox>
                                      <ReportCheckbox
                                          onChange={() => {
                                              handleMenuClick("S");
                                          }}
                                          checked={filters.includes("S") ? true : false}
                                      >
                      Secondary
                                      </ReportCheckbox>
                                      <ReportCheckbox
                                          onChange={() => {
                                              handleMenuClick(null);
                                          }}
                                          checked={filters.length === 0 ? true : false}
                                      >
                      All
                                      </ReportCheckbox>
                                  </>
                              )}
                          </SegCell>
                          <SegCell>
                Trade Area of <br />
                              <strong>{locationAddress}</strong>
                          </SegCell>
                          <SegCell>
                              {windowSize.width < deviceWidth.desktop ? `VS` : `COMPARED TO`}
                          </SegCell>
                          <SegCell
                              style={{
                                  width: !showPropensityColumn ? "185%" : "initial",
                              }}
                          >
                Average Trade Area of
                              <br />
                              <strong>
                                  {overviewRecord?.sampleCount} {brandName} Locations
                              </strong>
                          </SegCell>
                          {showPropensityColumn && (
                              <SegCell style={{ flexDirection: "row" }}>
                                  <span>
                    Propensity to be a<br />
                                      <strong>{brandName} Customer</strong>
                                  </span>
                                  <BuxTooltip content={propensityToolTipText} trigger="click" />
                              </SegCell>
                          )}
                          {!showPropensityColumn && <br />}
                      </SegRow>
                  </SegHead>
                  <SegBody>
                      {profRefDataRender && profRefDataRender.length > 0 ? (
                          profRefDataRender.map((siteSegRef: IProfRef) => {
                              const sourceOfTruthIdx: number
                  = profRefData.indexOf(siteSegRef);
                              const segment: ISegment = mosaicSegments.find(
                                  (seg: ISegment) => siteSegRef.profile.seg === seg.segmentKey
                              );
                              const brandSeg: IProfileRecord = brandProfile.find(
                                  (seg: IProfileRecord) => siteSegRef.profile.seg === seg.seg
                              );

                              return (
                                  <SegRow key={siteSegRef.profile.seg} ref={siteSegRef.ref}>
                                      <SegCell
                                          onClick={() => {
                                              setModalContent({
                                                  segment: segment,
                                                  siteSeg: siteSegRef.profile,
                                                  brandSeg: brandSeg,
                                              });
                                          }}
                                          style={{ cursor: "pointer" }}
                                      >
                                          <div>
                                              <SegImg
                                                  style={{ maxWidth: "85px", maxHeight: "85px" }}
                                                  src={`/Images/segmentation-imgs/${siteSegRef.profile.seg}.jpg`}
                                              />
                                          </div>
                                          <div>
                                              {windowSize.width < deviceWidth.desktop ? (
                                                  <>
                                                      <SegCellTextL style={{ marginTop: "6px" }}>
                                                          {siteSegRef.profile.seg}
                                                      </SegCellTextL>
                                                      <SegCellTextSmB
                                                          style={{ display: "flex", alignItems: "center" }}
                                                      >
                                                          {segment.name}{" "}
                                                          <FiExternalLink
                                                              size={15}
                                                              style={{ marginLeft: "3px" }}
                                                          />
                                                      </SegCellTextSmB>
                                                  </>
                                              ) : (
                                                  <SegDetails>
                                                      <SegCellTextL style={{ marginBottom: "10px" }}>
                                                          {siteSegRef.profile.seg}: {segment.name}
                                                      </SegCellTextL>
                                                      <div style={{ color: "#6d7278" }}>
                                                          {segment.description}
                                                      </div>
                                                      <SegLink>
                                                          <FiExternalLink
                                                              size={15}
                                                              style={{ marginRight: "7px" }}
                                                          />
                                                          <span>View Details</span>
                                                      </SegLink>
                                                  </SegDetails>
                                              )}
                                          </div>
                                      </SegCell>
                                      <div
                                          style={{ display: "contents" }}
                                          onMouseEnter={
                                              windowSize.width < deviceWidth.desktop
                                                  ? null
                                                  : () => setActive(sourceOfTruthIdx)
                                          }
                                          onMouseLeave={
                                              windowSize.width < deviceWidth.desktop
                                                  ? null
                                                  : () => setActive(null)
                                          }
                                      >
                                          <SegCell className="hide-show">
                                              <PctNumWrapper className="animated">
                                                  <SegCellTextL>
                                                      {calcHH(
                                                          benchmarkRecord.hhCnt,
                                                          siteSegRef.profile.pct
                                                      )}
                                                  </SegCellTextL>
                                                  <SegCellTextSm>households</SegCellTextSm>
                                              </PctNumWrapper>
                                              <PctNumWrapper className="animated">
                                                  <SegCellTextL>
                                                      {calcPctHH(siteSegRef.profile.pct)}%
                                                  </SegCellTextL>
                                                  <SegCellTextSm>of households</SegCellTextSm>
                                              </PctNumWrapper>
                                          </SegCell>
                                          <SegCell className="fill">
                                              <div></div>
                                          </SegCell>
                                          <SegCell
                                              className="hide-show"
                                              style={{
                                                  width: !showPropensityColumn ? "185%" : "initial",
                                              }}
                                          >
                                              <PctNumWrapper className="animated">
                                                  <SegCellTextL>
                                                      {calcHH(avgBrandHHCnt, brandSeg.pct)}
                                                  </SegCellTextL>
                                                  <SegCellTextSm>households</SegCellTextSm>
                                              </PctNumWrapper>
                                              <PctNumWrapper className="animated">
                                                  <SegCellTextL>
                                                      {calcPctHH(brandSeg.pct)}%
                                                  </SegCellTextL>
                                                  <SegCellTextSm>of households</SegCellTextSm>
                                              </PctNumWrapper>
                                          </SegCell>
                                      </div>
                                      {showPropensityColumn && (
                                          <>
                                              <SegCell>
                                                  <SegCirc
                                                      color={CustomerBreakoutColors[brandSeg.core]}
                                                  >
                                                      {/* GB - see bug #11881. I suspect this is passing in null. */}
                                                      <SegCircTextL>
                                                          {getPrettyNumber(brandSeg.index, 1)}X
                                                      </SegCircTextL>
                                                      <SegCircTextSm>
                                                          {determinePropensityText(
                                                              brandSeg.core
                                                          ).toUpperCase()}
                                                      </SegCircTextSm>
                                                  </SegCirc>
                                                  <SegCellTRAction
                                                      onClick={() => {
                                                          setActive(sourceOfTruthIdx);
                                                      }}
                                                  >
                            %
                                                  </SegCellTRAction>
                                              </SegCell>
                                          </>
                                      )}
                                      {!showPropensityColumn && <br />}
                                  </SegRow>
                              );
                          })
                      ) : (
                          <SegRow>
                              <EmptyResultsCell>
                                  <span>
                                      <GiAchillesHeel size={25} />
                                  </span>
                                  <span style={{ fontSize: "25px" }}>Ouch! No Results!</span>
                              </EmptyResultsCell>
                          </SegRow>
                      )}
                  </SegBody>
                  {/* TODO - this needs to be in it's own component */}
                  <Modal
                      title=""
                      visible={visibleModal}
                      onCancel={() => {
                          setVisibleModal(false);
                      }}
                      footer={null}
                      width="90%"
                      centered={true}
                      bodyStyle={{ padding: "40px 24px 24px" }}
                      style={{ maxWidth: "900px" }}
                  >
                      {modalContent && (
                          <>
                              <SegCardHead>
                                  <div>
                                      <SegImg
                                          src={`/Images/segmentation-imgs/${modalContent.segment.segmentKey}.jpg`}
                                      />
                                  </div>
                                  <div>
                                      <SegCellTextL style={{ marginBottom: "10px" }}>
                                          {modalContent.segment.segmentKey}:{" "}
                                          {modalContent.segment.name}
                                      </SegCellTextL>
                                      <div style={{ fontSize: "15px" }}>
                                          {modalContent.segment.description}
                                      </div>
                                  </div>
                              </SegCardHead>
                              <div>
                                  <SegContentSecHeader>Who We Are</SegContentSecHeader>
                                  <div>
                                      <SegCardHeaderRow>
                                          <SegCardCol></SegCardCol>
                                          <SegCardCol></SegCardCol>
                                          <SegCardSpacer />
                                          <SegCardCol>Index Value</SegCardCol>
                                          <SegCardSpacer />
                                          <SegCardCol>
                                              {modalContent.segment.segmentKey}%
                                          </SegCardCol>
                                      </SegCardHeaderRow>
                                  </div>
                                  <div>
                                      {modalContent.segment.segmentDetails.map((det) => {
                                          return (
                                              <SegCardRow key={det.idx}>
                                                  <SegCardCol>
                                                      {findSegIcon(det.varNm)}
                                                      {det.varNm}:
                                                  </SegCardCol>
                                                  <SegCardCol>{det.varVal}</SegCardCol>
                                                  <SegCardSpacer />
                                                  <SegCardCol>{det.idx}</SegCardCol>
                                                  <SegCardSpacer />
                                                  <SegCardCol>
                                                      {getPrettyNumber(det.pct, 1)}%
                                                  </SegCardCol>
                                              </SegCardRow>
                                          );
                                      })}
                                  </div>
                              </div>
                              <SegCardBreakoutContainer>
                                  <SegCardBreakoutItem>
                                      <span>{locationAddress}</span>
                                      <br />
                                      {modalContent.segment.segmentKey}s make up{" "}
                                      {calcHH(benchmarkRecord.hhCnt, modalContent.siteSeg.pct)} or{" "}
                                      {calcPctHH(modalContent.siteSeg.pct)}% of households
                                  </SegCardBreakoutItem>
                                  <SegCardBreakoutVerticalBreak />
                                  <SegCardBreakoutItem>
                                      <span>
                                          {overviewRecord?.sampleCount} {brandName}’s Locations
                                      </span>
                                      <br />
                                      {modalContent.segment.segmentKey}s make up{" "}
                                      {calcHH(avgBrandHHCnt, modalContent.brandSeg.pct)} or{" "}
                                      {calcPctHH(modalContent.brandSeg.pct)}% of households
                                  </SegCardBreakoutItem>
                                  <SegCardBreakoutVerticalBreak />
                                  <SegCardBreakoutItem>
                                      <span>
                                          {getPrettyNumber(modalContent.brandSeg.index, 1)}X{" "}
                                          {determinePropensityText(modalContent.brandSeg.core)}
                                      </span>
                                      <br />
                    Propensity to be a {brandName} Customer
                                  </SegCardBreakoutItem>
                              </SegCardBreakoutContainer>
                              <SegFooterSubText>
                  *Index score of the characteristics representation in{" "}
                                  {modalContent.segment.segmentKey}s compared to the US
                  population. Above 100 means the variable is over-represented
                  compared to the US population. Numbers on the right show the
                  percentage of {modalContent.segment.segmentKey}s that exhibit
                  this characteristic.
                              </SegFooterSubText>
                          </>
                      )}
                  </Modal>
              </AnimatedReportCard>
          </ReportScrollableSection>
      );
  };
